'use client';
import { useState, useRef, useCallback } from 'react';
import { calculateFinal, formatToIndianCurrency } from "@/_config";
import { RatingDetailsSidebar } from './RatingDetailsSidebar';
import { Dialog } from 'primereact/dialog';
import { useStoreConfig } from '@/_context/StoreConfigContext';

// ── Coupon savings calculator ─────────────────────────────────────────────────
const calcCouponSaving = (coupon, basePrice) => {
    if (!coupon || !basePrice) return 0;
    if (coupon.discountType === 'percentage') {
        const disc = Math.round(basePrice * coupon.discountValue / 100);
        return coupon.maxDiscount ? Math.min(disc, coupon.maxDiscount) : disc;
    }
    return Math.min(coupon.discountValue ?? 0, basePrice);
};

// ── Single coupon row inside overlay ─────────────────────────────────────────
const CouponDetailRow = ({ coupon, basePrice }) => {
    const [copied, setCopied] = useState(false);

    const copy = (e) => {
        e.stopPropagation();
        navigator.clipboard?.writeText(coupon.code).catch(() => {});
        setCopied(true);
        setTimeout(() => setCopied(false), 2200);
    };

    const saving = calcCouponSaving(coupon, basePrice);
    const savingText = coupon.discountType === 'percentage'
        ? `${coupon.discountValue}% off${coupon.maxDiscount ? ` (max ₹${coupon.maxDiscount})` : ''}`
        : `Flat ₹${coupon.discountValue} off`;

    return (
        <div style={{
            border: '1.5px dashed var(--primary-color)',
            borderRadius: 10,
            padding: '12px 14px',
            background: 'rgba(var(--primary-color-rgb, 0,100,200), 0.03)',
        }}>
            {/* code + copy row */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <i className="pi pi-tag" style={{ fontSize: '0.7rem', color: 'var(--primary-color)' }} />
                    <span style={{
                        fontFamily: 'monospace',
                        fontWeight: 800,
                        fontSize: '0.9rem',
                        letterSpacing: '0.1em',
                        color: 'var(--primary-color)',
                    }}>
                        {coupon.code}
                    </span>
                </div>
                <button
                    onClick={copy}
                    style={{
                        display: 'flex', alignItems: 'center', gap: 4,
                        padding: '4px 12px',
                        background: copied ? '#dcfce7' : 'var(--primary-color)',
                        border: copied ? '1px solid #86efac' : '1px solid var(--primary-color)',
                        borderRadius: 6,
                        cursor: 'pointer',
                        transition: 'all 0.18s',
                    }}
                >
                    <i className={`pi ${copied ? 'pi-check' : 'pi-copy'}`}
                        style={{ fontSize: '0.6rem', color: copied ? '#16a34a' : '#fff' }} />
                    <span style={{ fontSize: '0.7rem', fontWeight: 700, color: copied ? '#16a34a' : '#fff' }}>
                        {copied ? 'Copied!' : 'Copy'}
                    </span>
                </button>
            </div>

            {/* discount description */}
            <p style={{ margin: '0 0 6px', fontSize: '0.78rem', color: '#475569', fontWeight: 600 }}>
                {savingText}
                {coupon.minOrderValue > 0 && (
                    <span style={{ fontWeight: 400, color: '#94a3b8' }}> · Min order ₹{coupon.minOrderValue}</span>
                )}
            </p>

            {/* price after coupon */}
            {saving > 0 && basePrice > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.72rem', color: '#16a34a', fontWeight: 700 }}>
                    <i className="pi pi-check-circle" style={{ fontSize: '0.65rem' }} />
                    You save {formatToIndianCurrency(saving)} → Pay only {formatToIndianCurrency(basePrice - saving)}
                </div>
            )}

            {coupon.expiresAt && (
                <p style={{ margin: '6px 0 0', fontSize: '0.65rem', color: '#f59e0b' }}>
                    <i className="pi pi-clock" style={{ fontSize: '0.6rem', marginRight: 3 }} />
                    Expires {new Date(coupon.expiresAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                </p>
            )}
        </div>
    );
};

// ── Coupon section shown in the product card ──────────────────────────────────
export const CouponSection = ({ coupons = [], price }) => {
    const [visible, setVisible] = useState(false);
    if (!coupons.length) return null;

    const basePrice = Number(price) || 0;
    const label = coupons.length === 1 ? '1 Coupon' : `${coupons.length}+ Coupons`;

    return (
        <>
            <Dialog
                visible={visible}
                onHide={() => setVisible(false)}
                header={
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        background: 'var(--primary-color)',
                        margin: '-1rem -1.5rem 0',
                        padding: '14px 20px',
                        borderRadius: '10px 10px 0 0',
                    }}>
                        <i className="pi pi-tag" style={{ fontSize: '1rem', color: '#fff' }} />
                        <span style={{ fontSize: '1rem', fontWeight: 700, color: '#fff' }}>Available Coupons</span>
                    </div>
                }
                style={{ width: '92vw', maxWidth: 420 }}
                pt={{ header: { style: { padding: 0, border: 'none' } }, content: { style: { paddingTop: '1rem' } } }}
                dismissableMask
                draggable={false}
                resizable={false}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {coupons.map((c, i) => (
                        <CouponDetailRow key={c.code ?? i} coupon={c} basePrice={basePrice} />
                    ))}
                </div>
                <div style={{
                    marginTop: 16,
                    padding: '10px 14px',
                    background: '#f8fafc',
                    borderRadius: 8,
                    display: 'flex', alignItems: 'center', gap: 6,
                }}>
                    <i className="pi pi-info-circle" style={{ fontSize: '0.75rem', color: 'var(--primary-color)' }} />
                    <span style={{ fontSize: '0.72rem', color: '#64748b' }}>
                        Copy the coupon code and apply it at checkout to avail the discount.
                    </span>
                </div>
            </Dialog>

            {/* ── Card row ── */}
            <div
                onClick={() => setVisible(true)}
                style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    cursor: 'pointer',
                    borderRadius: 8,
                    overflow: 'hidden',
                    border: '1px solid rgba(var(--primary-color-rgb, 0,100,200), 0.25)',
                    marginTop: 6,
                }}
            >
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 5,
                    background: 'var(--primary-color)',
                    padding: '5px 10px',
                    flexShrink: 0,
                }}>
                    <i className="pi pi-tag" style={{ fontSize: '0.65rem', color: '#fff' }} />
                    <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#fff', whiteSpace: 'nowrap' }}>
                        Available Coupon
                    </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 10px' }}>
                    <span style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--primary-color)' }}>
                        {label}
                    </span>
                    <i className="pi pi-chevron-right" style={{ fontSize: '0.55rem', color: 'var(--primary-color)' }} />
                </div>
            </div>
        </>
    );
};

export const RatingBadge = ({ avg, count, product }) => {
    const [hovered,      setHovered]      = useState(false);
    const [sidebarOpen,  setSidebarOpen]  = useState(false);
    const [hoverData,    setHoverData]    = useState(null);
    const [hoverLoading, setHoverLoading] = useState(false);
    const fetchedRef    = useRef(false);
    const timerRef      = useRef(null);
    const productId     = product?._id;

    const handleMouseEnter = useCallback(() => {
        timerRef.current = setTimeout(() => {
            setHovered(true);
            if (productId && !fetchedRef.current) {
                fetchedRef.current = true;
                setHoverLoading(true);
                fetch(`/api/review/${productId}?limit=50`)
                    .then(r => r.json())
                    .then(json => setHoverData(json?.data || null))
                    .catch(() => {})
                    .finally(() => setHoverLoading(false));
            }
        }, 180);
    }, [productId]);

    const handleMouseLeave = useCallback(() => {
        clearTimeout(timerRef.current);
        setHovered(false);
    }, []);

    if (!avg || Number(avg) === 0) return null;

    const reviews = hoverData?.reviews || [];
    const dist    = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    reviews.forEach(r => { if (dist[r.rating] !== undefined) dist[r.rating]++; });
    const total = reviews.length;

    return (
        <>
            {productId && (
                <RatingDetailsSidebar
                    product={product}
                    visible={sidebarOpen}
                    onHide={() => setSidebarOpen(false)}
                />
            )}
            <div style={{ position: 'relative', display: 'inline-flex' }}>
                <span
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    onClick={() => productId && setSidebarOpen(true)}
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        background: 'rgba(22,163,74,0.10)',
                        border: '1px solid rgba(22,163,74,0.30)',
                        borderRadius: '999px',
                        padding: '3px 10px',
                        fontSize: '0.78rem',
                        fontWeight: 700,
                        color: '#15803d',
                        whiteSpace: 'nowrap',
                        cursor: productId ? 'pointer' : 'default',
                        userSelect: 'none',
                    }}
                >
                    <i className="pi pi-star-fill" style={{ fontSize: '0.62rem', color: '#f59e0b' }} />
                    {Math.floor(Number(avg))}+
                </span>

                {/* ── Hover overlay ── */}
                {hovered && (
                    <div className="hover-tooltip" style={{
                        position: 'absolute',
                        bottom: 'calc(100% + 10px)',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 9999,
                        background: '#fff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '14px',
                        padding: '14px 16px',
                        minWidth: '210px',
                        boxShadow: '0 8px 28px rgba(0,0,0,0.13)',
                        pointerEvents: 'none',
                    }}>
                        {/* caret */}
                        <div style={{
                            position: 'absolute',
                            bottom: '-7px',
                            left: '50%',
                            marginLeft: '-7px',
                            width: '13px',
                            height: '13px',
                            background: '#fff',
                            borderRight: '1px solid #e2e8f0',
                            borderBottom: '1px solid #e2e8f0',
                            transform: 'rotate(45deg)',
                        }} />

                        {/* avg + stars */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                            <div style={{ fontSize: '2rem', fontWeight: 800, color: '#1e293b', lineHeight: 1 }}>
                                {Number(avg).toFixed(1)}
                            </div>
                            <div>
                                <div style={{ display: 'flex', gap: '2px' }}>
                                    {[1,2,3,4,5].map(s => (
                                        <i key={s}
                                            className={`pi ${s <= Math.round(Number(avg)) ? 'pi-star-fill' : 'pi-star'}`}
                                            style={{ fontSize: '0.65rem', color: s <= Math.round(Number(avg)) ? '#f59e0b' : '#e2e8f0' }}
                                        />
                                    ))}
                                </div>
                                <div style={{ fontSize: '0.63rem', color: '#94a3b8', marginTop: '3px' }}>
                                    {count} {count === 1 ? 'rating' : 'ratings'}
                                </div>
                            </div>
                        </div>

                        {/* distribution bars */}
                        {hoverLoading ? (
                            <div style={{ display: 'flex', justifyContent: 'center', padding: '6px 0' }}>
                                <i className="pi pi-spin pi-spinner" style={{ color: '#f59e0b', fontSize: '0.9rem' }} />
                            </div>
                        ) : total > 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                {[5,4,3,2,1].map(s => {
                                    const pct      = Math.round((dist[s] / total) * 100);
                                    const barColor = s >= 4 ? '#10b981' : s === 3 ? '#f59e0b' : '#ef4444';
                                    return (
                                        <div key={s} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <span style={{ fontSize: '0.63rem', color: '#64748b', width: '18px', textAlign: 'right', flexShrink: 0 }}>
                                                {s}★
                                            </span>
                                            <div style={{ flex: 1, height: '6px', background: '#f1f5f9', borderRadius: '99px', overflow: 'hidden' }}>
                                                <div style={{
                                                    height: '100%',
                                                    width: `${pct}%`,
                                                    background: barColor,
                                                    borderRadius: '99px',
                                                    transition: 'width 0.5s ease',
                                                }} />
                                            </div>
                                            <span style={{ fontSize: '0.6rem', color: '#94a3b8', width: '26px', textAlign: 'right', flexShrink: 0 }}>
                                                {pct}%
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : null}

                        {productId && (
                            <div style={{
                                marginTop: '10px',
                                paddingTop: '8px',
                                borderTop: '1px solid #f1f5f9',
                                fontSize: '0.62rem',
                                color: '#94a3b8',
                                textAlign: 'center',
                            }}>
                                Click to see all reviews
                            </div>
                        )}
                    </div>
                )}
            </div>
        </>
    );
};

// ── Coupon hover popup with price breakdown ───────────────────────────────────
// ── Per-coupon tab content ────────────────────────────────────────────────────
const CouponTabContent = ({ coupon, numPrice, numMrp }) => {
    const [copied, setCopied] = useState(false);

    const copyCode = (e) => {
        e.stopPropagation();
        navigator.clipboard?.writeText(coupon.code).catch(() => {});
        setCopied(true);
        setTimeout(() => setCopied(false), 2200);
    };

    const offerDisc    = numMrp > numPrice ? Math.round(numMrp - numPrice) : 0;
    const couponSaving = calcCouponSaving(coupon, numPrice);
    const subTotal     = numPrice - couponSaving;

    const discLabel = coupon.discountType === 'percentage'
        ? `${coupon.discountValue}% off${coupon.maxDiscount ? ` (max ₹${coupon.maxDiscount})` : ''}`
        : `Flat ₹${coupon.discountValue} off`;

    const rows = [
        { label: 'MRP',       value: formatToIndianCurrency(numMrp)                           },
        { label: 'Price',     value: formatToIndianCurrency(numPrice),      bold: true         },
        { label: 'Discount',  value: `− ${formatToIndianCurrency(offerDisc)}`,  red: true      },
        { label: 'Total',     value: formatToIndianCurrency(numPrice),      bold: true         },
        { label: 'Coupon',    value: `− ${formatToIndianCurrency(couponSaving)}`, green: true  },
        { label: 'Sub Total', value: formatToIndianCurrency(subTotal),      sub: true, bold: true },
    ];

    return (
        <div>
            {/* Coupon code row */}
            <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                padding: '12px 16px 10px',
                borderBottom: '1px dashed #e2e8f0',
            }}>
                <div>
                    <span style={{
                        fontFamily: 'monospace', fontWeight: 800, fontSize: '1rem',
                        letterSpacing: '0.12em', color: 'var(--primary-color)',
                        background: 'rgba(0,0,0,0.04)', borderRadius: 6, padding: '3px 10px',
                        display: 'inline-block',
                    }}>
                        {coupon.code}
                    </span>
                    <div style={{ fontSize: '0.68rem', color: '#64748b', marginTop: 4 }}>
                        {discLabel}
                        {coupon.minOrderValue > 0 && (
                            <span style={{ color: '#94a3b8' }}> · Min order ₹{coupon.minOrderValue}</span>
                        )}
                    </div>
                    {coupon.expiresAt && (
                        <div style={{ fontSize: '0.63rem', color: '#f59e0b', marginTop: 2 }}>
                            <i className="pi pi-clock" style={{ fontSize: '0.58rem', marginRight: 3 }} />
                            Expires {new Date(coupon.expiresAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </div>
                    )}
                </div>
                <button
                    onClick={copyCode}
                    style={{
                        display: 'flex', alignItems: 'center', gap: 5,
                        padding: '6px 14px',
                        background: copied ? '#dcfce7' : 'var(--primary-color)',
                        border: copied ? '1px solid #86efac' : '1px solid var(--primary-color)',
                        borderRadius: 7, cursor: 'pointer', transition: 'all 0.18s', flexShrink: 0,
                    }}
                >
                    <i className={`pi ${copied ? 'pi-check' : 'pi-copy'}`}
                        style={{ fontSize: '0.65rem', color: copied ? '#16a34a' : '#fff' }} />
                    <span style={{ fontSize: '0.72rem', fontWeight: 700, color: copied ? '#16a34a' : '#fff' }}>
                        {copied ? 'Copied!' : 'Copy'}
                    </span>
                </button>
            </div>

            {/* Price breakdown table */}
            <div style={{ padding: '10px 16px 8px' }}>
                {rows.map((r, i) => (
                    <div key={i} style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: r.sub ? '8px 0 0' : '4px 0',
                        borderTop: r.sub ? '1px solid #e2e8f0' : 'none',
                        marginTop: r.sub ? 6 : 0,
                    }}>
                        <span style={{
                            fontSize: r.sub ? '0.8rem' : '0.72rem',
                            fontWeight: r.bold ? 700 : 400,
                            color: r.sub ? '#1e293b' : '#64748b',
                        }}>
                            {r.label}
                        </span>
                        <span style={{
                            fontSize: r.sub ? '0.88rem' : '0.75rem',
                            fontWeight: r.bold ? 800 : 500,
                            color: r.green ? '#16a34a' : r.red ? '#ef4444' : (r.sub ? 'var(--primary-color)' : '#1e293b'),
                        }}>
                            {r.value}
                        </span>
                    </div>
                ))}
            </div>

            {/* Checkout hint */}
            <div style={{
                margin: '4px 16px 14px',
                padding: '8px 12px',
                background: '#f8fafc',
                borderRadius: 8,
                display: 'flex', alignItems: 'flex-start', gap: 6,
            }}>
                <i className="pi pi-info-circle" style={{ fontSize: '0.65rem', color: 'var(--primary-color)', marginTop: 2, flexShrink: 0 }} />
                <span style={{ fontSize: '0.65rem', color: '#64748b', lineHeight: 1.6 }}>
                    Coupon can be applied at checkout — you'll see the coupon code on the checkout page.
                </span>
            </div>
        </div>
    );
};

// ── Coupon modal (Dialog-based, no clipping from parent overflow:hidden) ─────
const CouponModal = ({ coupons, price, mrp, open, onClose, activeTab, setActiveTab }) => {
    const visibleCoupons = coupons.slice(0, 4);
    const numPrice       = Number(price) || 0;
    const numMrp         = Number(mrp) || numPrice;

    return (
        <Dialog
            visible={open}
            onHide={onClose}
            header={
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    background: 'var(--primary-color)',
                    padding: '14px 20px',
                }}>
                    <i className="pi pi-tag" style={{ fontSize: '1rem', color: '#fff' }} />
                    <div>
                        <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#fff' }}>
                            Special Coupons for You
                        </div>
                        <div style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.75)', marginTop: 2 }}>
                            Apply at checkout to save instantly
                        </div>
                    </div>
                </div>
            }
            style={{ width: '94vw', maxWidth: 400 }}
            pt={{
                root:    { style: { borderRadius: '12px', overflow: 'hidden' } },
                header:  { style: { padding: 0, background: 'var(--primary-color)' } },
                content: { style: { padding: 0 } },
            }}
            dismissableMask
            draggable={false}
            resizable={false}
        >
            {/* Tabs — only when 2+ coupons */}
            {visibleCoupons.length > 1 && (
                <div style={{ display: 'flex', borderBottom: '2px solid #e2e8f0', background: '#fafafa' }}>
                    {visibleCoupons.map((_, i) => (
                        <button
                            key={i}
                            onClick={() => setActiveTab(i)}
                            style={{
                                flex: 1, padding: '9px 4px',
                                border: 'none',
                                borderBottom: activeTab === i ? '2px solid var(--primary-color)' : '2px solid transparent',
                                marginBottom: -2, background: 'none', cursor: 'pointer',
                                fontSize: '0.68rem',
                                fontWeight: activeTab === i ? 700 : 500,
                                color: activeTab === i ? 'var(--primary-color)' : '#64748b',
                                transition: 'all 0.15s',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            <i className="pi pi-tag" style={{ fontSize: '0.55rem', marginRight: 3 }} />
                            Coupon {i + 1}
                        </button>
                    ))}
                </div>
            )}

            <CouponTabContent
                key={activeTab}
                coupon={visibleCoupons[activeTab]}
                numPrice={numPrice}
                numMrp={numMrp}
            />
        </Dialog>
    );
};

// ── Pay Online Modal ──────────────────────────────────────────────────────────
const PayOnlineModal = ({ open, onClose, numPrice, bestCouponSaving, discountForAll, prepaidDiscount }) => {
    const couponSaving   = bestCouponSaving;
    const storeSaving    = discountForAll;
    const prepaidSaving  = prepaidDiscount;
    const totalSaving    = couponSaving + storeSaving + prepaidSaving;
    const finalPay       = Math.max(0, numPrice - totalSaving);

    const rows = [
        { label: 'Price',            value: formatToIndianCurrency(numPrice),          bold: true },
        ...(couponSaving > 0  ? [{ label: 'Coupon discount',  value: `− ${formatToIndianCurrency(couponSaving)}`,  green: true }] : []),
        ...(storeSaving > 0   ? [{ label: 'Store discount',   value: `− ${formatToIndianCurrency(storeSaving)}`,   green: true }] : []),
        ...(prepaidSaving > 0 ? [{ label: 'Prepaid discount', value: `− ${formatToIndianCurrency(prepaidSaving)}`, blue: true  }] : []),
    ];

    return (
        <Dialog
            visible={open}
            onHide={onClose}
            header={
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    background: '#14b8a6', padding: '14px 20px',
                }}>
                    <i className="pi pi-credit-card" style={{ fontSize: '1rem', color: '#fff' }} />
                    <div>
                        <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#fff' }}>Pay Online & Save More</div>
                        <div style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.75)', marginTop: 2 }}>
                            Extra savings on prepaid orders
                        </div>
                    </div>
                </div>
            }
            style={{ width: '94vw', maxWidth: 380 }}
            pt={{
                root:    { style: { borderRadius: '12px', overflow: 'hidden' } },
                header:  { style: { padding: 0, background: '#14b8a6' } },
                content: { style: { padding: 0 } },
            }}
            dismissableMask
            draggable={false}
            resizable={false}
        >
            {/* Price breakdown */}
            <div style={{ padding: '16px 18px 0' }}>
                {rows.map((r, i) => (
                    <div key={i} style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '5px 0',
                        borderBottom: i < rows.length - 1 ? '1px dashed #f1f5f9' : 'none',
                    }}>
                        <span style={{ fontSize: '0.75rem', fontWeight: r.bold ? 700 : 400, color: '#64748b' }}>{r.label}</span>
                        <span style={{ fontSize: '0.78rem', fontWeight: r.bold ? 700 : 600, color: r.green ? '#16a34a' : r.blue ? '#14b8a6' : '#1e293b' }}>
                            {r.value}
                        </span>
                    </div>
                ))}
            </div>

            {/* Final pay row */}
            <div style={{
                margin: '14px 18px',
                padding: '12px 16px',
                background: 'linear-gradient(90deg, #f0fdfa, #ccfbf1)',
                border: '1.5px solid #5eead4',
                borderRadius: 10,
            }}>
                <div style={{ fontSize: '0.72rem', color: '#0f766e', fontWeight: 600, marginBottom: 4 }}>
                    <i className="pi pi-credit-card" style={{ fontSize: '0.65rem', marginRight: 5 }} />
                    Pay online — pay only
                </div>
                <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#14b8a6', letterSpacing: '-0.5px' }}>
                    {formatToIndianCurrency(finalPay)}
                </div>
                {prepaidSaving > 0 && (
                    <div style={{ fontSize: '0.68rem', color: '#0d9488', marginTop: 4, fontWeight: 600 }}>
                        <i className="pi pi-check-circle" style={{ fontSize: '0.62rem', marginRight: 4, color: '#16a34a' }} />
                        Extra {formatToIndianCurrency(prepaidSaving)} off on prepaid orders
                    </div>
                )}
            </div>

            {/* Hint */}
            <div style={{
                margin: '0 18px 16px',
                padding: '8px 12px',
                background: '#f8fafc', borderRadius: 8,
                display: 'flex', alignItems: 'flex-start', gap: 6,
            }}>
                <i className="pi pi-info-circle" style={{ fontSize: '0.65rem', color: '#14b8a6', marginTop: 2, flexShrink: 0 }} />
                <span style={{ fontSize: '0.65rem', color: '#64748b', lineHeight: 1.6 }}>
                    Prepaid discount is applied automatically when you pay online at checkout.
                </span>
            </div>
        </Dialog>
    );
};

export const PriceInfo = ({ offer, price, mrp, coupons = [], compact = false }) => {
    const [couponOpen,    setCouponOpen]    = useState(false);
    const [activeTab,     setActiveTab]     = useState(0);
    const [payOnlineOpen, setPayOnlineOpen] = useState(false);
    const { discountForAll = 0, prepaidDiscount = 0 } = useStoreConfig();

    const isPriceRange = typeof price === 'string' && price?.indexOf('-') >= 0;

    if (isPriceRange) {
        return (
            <div className="flex flex-column gap-1">
                <div className="flex align-items-baseline gap-2 flex-wrap">
                    <span style={{ fontSize: '1.9rem', fontWeight: 800, color: 'var(--secondary-color)', letterSpacing: '-0.5px' }}>
                        ₹{price}
                    </span>
                    {mrp && (
                        <span style={{ fontSize: '0.78rem', color: 'var(--text-color-secondary)' }}>
                            M.R.P <span className="line-through">{formatToIndianCurrency(mrp)}</span>
                        </span>
                    )}
                </div>
                <span style={{ fontSize: '0.7rem', color: 'var(--secondary-color)', opacity: 0.85 }}>
                    Price may vary by variant
                </span>
            </div>
        );
    }

    const numPrice    = Number(price) || 0;
    const numMrp      = Number(mrp) || numPrice;
    const finalPrice  = calculateFinal(offer, numPrice, mrp);
    const baseMrp     = mrp ?? price;
    const savingsAmt  = mrp && mrp > numPrice ? Math.round(mrp - numPrice) : 0;
    const hasDiscount = offer > 0;

    // Best saving = highest coupon saving + discountForAll (always applied) + prepaidDiscount (optional)
    const bestCouponSaving  = coupons.length > 0
        ? Math.max(...coupons.map(c => calcCouponSaving(c, numPrice)))
        : 0;
    const bestSaving        = bestCouponSaving + discountForAll;
    const bestSavingPrepaid = bestCouponSaving + discountForAll + prepaidDiscount;
    const couponPrice       = Math.max(0, numPrice - bestSaving);
    const couponPricePrepaid = Math.max(0, numPrice - bestSavingPrepaid);

    return (
        <div className="flex flex-column" style={{ gap: '0.45rem', flex: compact ? '1' : undefined, minWidth: compact ? 0 : undefined, width: compact ? undefined : '100%' }}>

            {/* ── Coupon Dialog (portal-rendered, no clipping) ── */}
            {coupons.length > 0 && (
                <CouponModal
                    coupons={coupons}
                    price={numPrice}
                    mrp={numMrp}
                    open={couponOpen}
                    onClose={() => setCouponOpen(false)}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                />
            )}

            {/* ── Pay Online Dialog ── */}
            <PayOnlineModal
                open={payOnlineOpen}
                onClose={() => setPayOnlineOpen(false)}
                numPrice={numPrice}
                bestCouponSaving={bestCouponSaving}
                discountForAll={discountForAll}
                prepaidDiscount={prepaidDiscount}
            />

            {/* ── Main price row ───────────────────────────── */}
            <div className="flex align-items-center gap-3 flex-wrap">
                <span className="text-primary" style={{ fontSize: '2rem', fontWeight: 800, letterSpacing: '-0.5px', lineHeight: 1 }}>
                    {finalPrice}
                </span>
                {hasDiscount && (
                    <span className="bg-primary" style={{
                        color: '#fff', padding: '4px 12px', borderRadius: '999px',
                        fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.4px',
                        boxShadow: '0 2px 8px rgba(9,9,9,0.35)', whiteSpace: 'nowrap',
                    }}>
                        {offer}% OFF
                    </span>
                )}
            </div>

            {/* ── Row 1: M.R.P left | You save right ── */}
            {baseMrp && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <span style={{ fontSize: '0.78rem', color: 'var(--primary-color)', opacity: 0.7 }}>
                        M.R.P: <span className="line-through">{formatToIndianCurrency(baseMrp)}</span>
                    </span>
                    {savingsAmt > 0 && (
                        <span style={{
                            fontSize: '0.78rem', fontWeight: 700, color: '#0c7a70',
                            background: 'rgba(12,122,112,0.1)', border: '1px solid rgba(12,122,112,0.28)',
                            padding: '2px 10px', borderRadius: '999px', whiteSpace: 'nowrap',
                        }}>
                            You save {formatToIndianCurrency(savingsAmt)}
                        </span>
                    )}
                </div>
            )}

            {/* ── Row 2: With Coupon left | Pay online right ── */}
            {coupons.length > 0 && bestSaving > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <button
                        onClick={() => { setActiveTab(0); setCouponOpen(true); }}
                        style={{
                            display: 'inline-flex', alignItems: 'center', gap: 3,
                            background: '#888888', border: 'none',
                            borderRadius: '999px', cursor: 'pointer',
                            padding: '3px 9px', margin: 0,
                          
                        }}
                    >
                        <i className="pi pi-tag" style={{ fontSize: '0.55rem', color: '#fff' }} />
                        <span style={{ fontSize: '0.65rem', fontWeight: 700, color: '#fff', whiteSpace: 'nowrap' }}>
                            With Coupon {formatToIndianCurrency(couponPrice)}
                        </span>
                    </button>
                    <button
                        onClick={() => setPayOnlineOpen(true)}
                        style={{
                            display: 'inline-flex', alignItems: 'center', gap: 3,
                            background: '#888888', border: 'none',
                            borderRadius: '999px', cursor: 'pointer',
                            padding: '3px 9px', margin: 0,
                        }}
                    >
                        <i className="pi pi-credit-card" style={{ fontSize: '0.55rem', color: '#fff' }} />
                        <span style={{ fontSize: '0.65rem', fontWeight: 700, color: '#fff', whiteSpace: 'nowrap' }}>
                            Pay online {formatToIndianCurrency(couponPricePrepaid)}
                        </span>
                    </button>
                </div>
            )}



        </div>
    );
};
